import { Box, Typography, useTheme } from "@mui/material";
import { federalWasteGeneratorStatusToLabel } from "encamp-shared/src/waste";
import {
  RcraInfoFederalWasteGeneratorStatus,
  WasteShipmentChartsQuery,
} from "generated-graphql/graphql";
import { useMemo } from "react";

import {
  GeneratorStatusKind,
  getFederalGeneratorStatusColorSettings,
} from "../../getFederalGeneratorStatusColorSettings";

const ClassificationCell = ({
  status,
  statusKind,
  zIndex,
  isEndOfGroup,
  isStartOfGroup,
}: {
  status: RcraInfoFederalWasteGeneratorStatus | null;
  statusKind: GeneratorStatusKind;
  zIndex: number;
  isEndOfGroup: boolean;
  isStartOfGroup: boolean;
}) => {
  const { textColor, backgroundColor } = useMemo(
    () => getFederalGeneratorStatusColorSettings(status, statusKind),
    [status, statusKind]
  );

  return (
    <Box
      sx={{
        minHeight: 32,
        marginLeft: isStartOfGroup ? 0.25 : 0,
        marginRight: isEndOfGroup ? 0.25 : 0,
        backgroundColor: backgroundColor,
        display: "flex",
        paddingLeft: 1,
        paddingY: 1,
        alignItems: "center",
        justifyContent: "flex-start",
        cursor: "pointer",
        transition: "all 0.2s ease",
        position: "relative",
        "&:hover": {
          opacity: 0.8,
          transform: "scale(1.02)",
        },
        zIndex,
      }}
    >
      {isStartOfGroup && (
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.8125rem",
            color: textColor,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
          }}
        >
          {federalWasteGeneratorStatusToLabel(status ?? null)}
        </Typography>
      )}
    </Box>
  );
};

type ClassificationBarsProps = {
  data: WasteShipmentChartsQuery["facilityWasteShipmentCharts"]["wasteShipmentsByMonth"];
  hoveredMonth?: string | null;
  onMonthHover?: (month: string) => void;
  onMonthLeave?: () => void;
  onMonthClick?: (month: string) => void;
};

// Classification bars component
export const ClassificationBars = ({
  data,
  hoveredMonth,
  onMonthHover,
  onMonthLeave,
  onMonthClick,
}: ClassificationBarsProps) => {
  const theme = useTheme();
  const statusByMonth = useMemo(() => {
    return data.byMonth.map((monthData, index) => {
      const nextMonth = data.byMonth[index + 1];
      const prevMonth = data.byMonth[index - 1];
      const calculatedEndOfGroup =
        !nextMonth ||
        nextMonth.calculatedFederalGeneratorStatus !== monthData.calculatedFederalGeneratorStatus;
      const calculatedStartOfGroup =
        !prevMonth ||
        prevMonth.calculatedFederalGeneratorStatus !== monthData.calculatedFederalGeneratorStatus;
      const reportedEndOfGroup =
        !nextMonth ||
        nextMonth.reportedFederalGeneratorStatus !== monthData.reportedFederalGeneratorStatus;
      const reportedStartOfGroup =
        !prevMonth ||
        prevMonth.reportedFederalGeneratorStatus !== monthData.reportedFederalGeneratorStatus;
      return {
        month: monthData.month,
        calculatedEndOfGroup,
        calculatedStartOfGroup,
        reportedEndOfGroup,
        reportedStartOfGroup,
        reportedFederalGeneratorStatus: monthData.reportedFederalGeneratorStatus,
        calculatedFederalGeneratorStatus: monthData.calculatedFederalGeneratorStatus,
      };
    });
  }, [data]);

  return (
    <Box mr={2.5} mb={2}>
      {/* Row for Reported status */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Box
          sx={{
            width: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            pr: 1,
            minHeight: 32,
          }}
        >
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Reported</Typography>
        </Box>
        <Box sx={{ display: "flex", flex: 1 }}>
          {statusByMonth.map((monthData, index) => {
            return (
              <Box
                key={`reported-${index}`}
                onMouseEnter={() => onMonthHover?.(monthData.month)}
                onMouseLeave={onMonthLeave}
                onClick={() => onMonthClick?.(monthData.month)}
                sx={{
                  position: "relative",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                {monthData.month === hoveredMonth && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -40, // Extend up to cover just the x-axis gap
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: theme.palette.grey[500],
                      opacity: 0.2,
                      zIndex: 1000,
                      pointerEvents: "none",
                    }}
                  />
                )}
                <ClassificationCell
                  zIndex={3}
                  status={monthData.reportedFederalGeneratorStatus ?? null}
                  statusKind={GeneratorStatusKind.REPORTED}
                  isEndOfGroup={monthData.reportedEndOfGroup}
                  isStartOfGroup={monthData.reportedStartOfGroup}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Row for Calculated status */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            width: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            pr: 1,
            minHeight: 32,
          }}
        >
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Calculated</Typography>
        </Box>
        <Box sx={{ display: "flex", flex: 1 }}>
          {statusByMonth.map((monthData, index) => {
            return (
              <Box
                key={`calculated-${index}`}
                onMouseEnter={() => onMonthHover?.(monthData.month)}
                onMouseLeave={onMonthLeave}
                onClick={() => onMonthClick?.(monthData.month)}
                sx={{
                  position: "relative",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                {monthData.month === hoveredMonth && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: -8,
                      backgroundColor: theme.palette.grey[500],
                      opacity: 0.2,
                      zIndex: 1000,
                      pointerEvents: "none",
                    }}
                  />
                )}
                <ClassificationCell
                  zIndex={3}
                  status={monthData.calculatedFederalGeneratorStatus ?? null}
                  statusKind={GeneratorStatusKind.CALCULATED}
                  isEndOfGroup={monthData.calculatedEndOfGroup}
                  isStartOfGroup={monthData.calculatedStartOfGroup}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
